import type { FocusShape } from '@unifyapps/defs/types/page/walkthrough';
import { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useLatest } from 'react-use';

/**
 * This is like the position and size of a box we want to highlight.
 * Imagine you have a photo and want to frame a specific part of it.
 * This tells us where to put the frame and how big it should be.
 */
type StageDefinition = {
  x: number; // How far from the left edge of the screen
  y: number; // How far from the top edge of the screen
  width: number; // How wide the highlight should be
  height: number; // How tall the highlight should be
};

/**
 * This function is like a special calculator that helps us make curved corners.
 * It's the mathematical formula that creates the smooth movement effect.
 *
 * Imagine you're throwing a ball - it starts slow, speeds up, then slows down again.
 * This function creates that natural-feeling movement.
 */
const easeInOutQuad = (t: number, b: number, c: number, d: number): number => {
  const halfTime = t / (d / 2);
  if (halfTime < 1) return (c / 2) * halfTime * halfTime + b;
  const adjustedTime = halfTime - 1;
  return (-c / 2) * (adjustedTime * (adjustedTime - 2) - 1) + b;
};

/**
 *
 * This is a custom hook that helps create and manage a highlight overlay.
 * Imagine you have a flashlight in a dark room - this hook controls the flashlight
 * to shine light on exactly the part of the page you want people to look at.
 */
export function useHighlightOverlay(
  containerEl: HTMLElement | null = null,
  focusShape: FocusShape | undefined = 'rectangle',
) {
  // These are like memory boxes where we store information
  const [stage, setStage] = useState<StageDefinition | null>(null);
  // we have this useLatest because we want state to be deps in the handleResize callback
  // but because it is an object, it will have a lot of new references and cause a lot of re-renders
  const latestStage = useLatest(stage);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for potential future use
  const [_prevStage, setPrevStage] = useState<StageDefinition | null>(null);
  const [animationState, setAnimationState] = useState<{
    isAnimating: boolean;
    animationProgress: number;
  }>({ isAnimating: false, animationProgress: 0 });

  const svgRef = useRef<SVGSVGElement | null>(null);
  const pathRef = useRef<SVGPathElement | null>(null);
  const [pathElement, setPathElement] = useState<SVGPathElement | null>(null);
  // Callback ref so pathElement state updates when the path mounts/unmounts,
  // enabling effects elsewhere to re-run when the DOM node is available
  const pathRefCallback = useCallback((el: SVGPathElement | null) => {
    pathRef.current = el;
    setPathElement(el);
  }, []);
  const animationRef = useRef<number | null>(null);

  /**
   * This function updates the animation state.
   * It's like a switch that turns the animation on and off.
   */
  const updateAnimationState = useCallback((progress: number, isActive: boolean) => {
    setAnimationState((_prevState) => {
      // If animation is not active, set final state
      if (!isActive) {
        return { isAnimating: false, animationProgress: 1 };
      }

      // Use the actual progress value for more granular control
      return { isAnimating: true, animationProgress: progress };
    });
  }, []);

  /**
   * This function creates the special shape we need for our highlight.
   *
   * Imagine you have a piece of black paper covering the whole screen.
   * Now you want to cut a hole in it to show just one part.
   * This function creates the instructions for exactly how to cut that hole.
   */
  const generateSvgPath = useCallback(
    (_stage: StageDefinition): string => {
      // Get the size of the container (arena/canvas) or fallback to window
      const containerRect = containerEl?.getBoundingClientRect();
      const containerX = containerRect?.width ?? window.innerWidth;
      const containerY = containerRect?.height ?? window.innerHeight;

      // Add some extra space around the element (like a picture frame's matting)
      const stagePadding = 8;

      // Calculate the size of our highlight area with the padding
      const stageWidth = _stage.width + stagePadding * 2;
      const stageHeight = _stage.height + stagePadding * 2;

      // Calculate center and radius for circle shape
      const centerX = _stage.x + _stage.width / 2;
      const centerY = _stage.y + _stage.height / 2;
      const radius = Math.max(stageWidth, stageHeight) / 2;

      // Calculate where to position our highlight
      const highlightBoxX = _stage.x - stagePadding;
      const highlightBoxY = _stage.y - stagePadding;

      if (focusShape === 'circle') {
        // Create a circular hole
        return `M${containerX},0L0,0L0,${containerY}L${containerX},${containerY}L${containerX},0Z
          M${centerX},${centerY}
          m-${radius},0
          a${radius},${radius} 0 1,0 ${radius * 2},0
          a${radius},${radius} 0 1,0 -${radius * 2},0 z`;
      }

      // Rectangle shape (default)
      // Make the corners rounded instead of sharp
      const stageRadius = 4;
      // Make sure our corner rounding isn't too big for small elements
      const limitedRadius = Math.min(stageRadius, stageWidth / 2, stageHeight / 2);
      const normalizedRadius = Math.floor(Math.max(limitedRadius, 0));

      // This is the magic code that creates our shape
      // It's like giving step-by-step cutting instructions:
      // 1. First, define the whole container as a black rectangle
      // 2. Then, cut out a rounded rectangle where our element is
      return `M${containerX},0L0,0L0,${containerY}L${containerX},${containerY}L${containerX},0Z
        M${highlightBoxX + normalizedRadius},${highlightBoxY} 
        h${stageWidth - normalizedRadius * 2} 
        a${normalizedRadius},${normalizedRadius} 0 0 1 ${normalizedRadius},${normalizedRadius} 
        v${stageHeight - normalizedRadius * 2} 
        a${normalizedRadius},${normalizedRadius} 0 0 1 -${normalizedRadius},${normalizedRadius} 
        h-${stageWidth - normalizedRadius * 2} 
        a${normalizedRadius},${normalizedRadius} 0 0 1 -${normalizedRadius},-${normalizedRadius} 
        v-${stageHeight - normalizedRadius * 2} 
        a${normalizedRadius},${normalizedRadius} 0 0 1 ${normalizedRadius},-${normalizedRadius} z`;
    },
    [containerEl, focusShape],
  );

  /**
   * This function creates a smooth animation when we move from highlighting
   * one element to highlighting another.
   *
   * Imagine a spotlight on a stage that needs to smoothly move from
   * one actor to another - this controls that movement.
   */
  const animateBetweenStages = useCallback(
    (from: StageDefinition, to: StageDefinition) => {
      if (!pathRef.current) return;

      // How long our animation should take (in milliseconds)
      const duration = 200;
      // When we started the animation
      const startTime = performance.now();

      // If we're already animating, stop that animation first
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      // Update the animation state to start the animation
      updateAnimationState(0, true);

      // This function runs many times during the animation to create smooth movement
      const animate = (currentTime: number) => {
        // How much time has passed since we started
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Update animation state with continuous progress value
        updateAnimationState(progress, true);

        // If animation is complete
        if (elapsed >= duration) {
          setStage(to);
          animationRef.current = null;
          updateAnimationState(1, false);
          pathRef.current?.setAttribute('d', generateSvgPath(to));
          return;
        }

        // Calculate current intermediate position
        const current: StageDefinition = {
          x: easeInOutQuad(elapsed, from.x, to.x - from.x, duration),
          y: easeInOutQuad(elapsed, from.y, to.y - from.y, duration),
          width: easeInOutQuad(elapsed, from.width, to.width - from.width, duration),
          height: easeInOutQuad(elapsed, from.height, to.height - from.height, duration),
        };

        // Update SVG path
        if (pathRef.current) {
          pathRef.current.setAttribute('d', generateSvgPath(current));
        }

        // Continue animation
        animationRef.current = requestAnimationFrame(animate);
      };

      animationRef.current = requestAnimationFrame(animate);
    },
    [generateSvgPath, updateAnimationState],
  );

  const handleResize = useCallback(() => {
    if (svgRef.current) {
      // Update the SVG canvas size to match the container size
      const containerRect = containerEl?.getBoundingClientRect();
      const containerX = containerRect?.width ?? window.innerWidth;
      const containerY = containerRect?.height ?? window.innerHeight;
      svgRef.current.setAttribute('viewBox', `0 0 ${containerX} ${containerY}`);

      // If we have a highlight area, update the path to match the new size
      if (latestStage.current && pathRef.current) {
        pathRef.current.setAttribute('d', generateSvgPath(latestStage.current));
      }
    }
  }, [generateSvgPath, containerEl, latestStage]);

  /**
   * This function handles when the window size changes.
   */
  useEffect(() => {
    const targetWindow = containerEl?.ownerDocument.defaultView ?? window;
    targetWindow.addEventListener('resize', handleResize);

    return () => targetWindow.removeEventListener('resize', handleResize);
  }, [handleResize, containerEl]);

  /**
   * This sets up our SVG (the overlay that darkens everything except our highlight)
   * whenever it changes or when we highlight a new element.
   *
   * Think of this as preparing our canvas before we start drawing.
   */
  useEffect(() => {
    if (!svgRef.current) return;

    // Set the size of our canvas to match the container
    const containerRect = containerEl?.getBoundingClientRect();
    const containerX = containerRect?.width ?? window.innerWidth;
    const containerY = containerRect?.height ?? window.innerHeight;

    // These attributes control how our SVG behaves
    svgRef.current.setAttribute('viewBox', `0 0 ${containerX} ${containerY}`);
    svgRef.current.setAttribute('xmlSpace', 'preserve');
    svgRef.current.setAttribute('version', '1.1');
    svgRef.current.setAttribute('preserveAspectRatio', 'xMinYMin slice');

    // If we have an element to highlight, draw the highlight shape
    if (stage && pathRef.current) {
      pathRef.current.setAttribute('d', generateSvgPath(stage));
    }
  }, [stage, generateSvgPath, containerEl]);

  /**
   * This is the main function that other components will use to highlight an element.
   *
   * Imagine you have a friend with a flashlight, and you say "Hey, shine the light on that picture."
   * This is the function that moves the flashlight to shine on the right spot.
   */
  const highlightElement = useCallback(
    (element: HTMLElement) => {
      // Get the exact position and size of the element we want to highlight
      const elementRect = element.getBoundingClientRect();

      // Get container position to calculate relative coordinates
      const containerRect = containerEl?.getBoundingClientRect();

      // Calculate position relative to container, or use window coordinates if no container
      const x = containerRect ? elementRect.left - containerRect.left : elementRect.left;
      const y = containerRect ? elementRect.top - containerRect.top : elementRect.top;

      // Create a definition for our highlight
      const newStage: StageDefinition = {
        x,
        y,
        width: elementRect.width,
        height: elementRect.height,
      };

      setStage((_prevStage) => {
        if (_prevStage && !isEqual(_prevStage, newStage)) {
          setPrevStage(_prevStage);
          animateBetweenStages(_prevStage, newStage);
        } else if (!_prevStage) {
          // First time highlight with no animation
          // Still trigger animation state for tooltip visibility logic
          updateAnimationState(1, false);
        }
        return newStage;
      });
    },
    [animateBetweenStages, updateAnimationState, containerEl],
  );

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return {
    svgRef,
    pathRef: pathRefCallback,
    pathElement,
    highlightElement,
    animationState,
    handleResize,
    stage,
  };
}
